import React, { useEffect, useRef } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Box, ThemeProvider } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import Eyebrow from '../marketing/components/Eyebrow';
import MillosChapterHeader from '../marketing/components/millos/MillosChapterHeader';
import MillosRenewalLoop from '../marketing/components/millos/MillosRenewalLoop';
import MillosLoop from '../marketing/components/millos/MillosLoop';
import {
  homepageStyles, homepageTheme, marketingDarkStyles, marketingDarkPalette as D,
  marketingColors as C, marketingFonts as F,
} from '../theme_landing_page_2';

const certificateSteps = [
  { title: 'Reads and organises', body: 'Upload a PDF or connect your inbox. MillOS pulls out the type, dates and address, and files it under the right property.' },
  { title: 'Tracks what is due', body: 'See at a glance what is valid, expiring soon or overdue, with reminders before anything lapses.' },
  { title: 'Coordinates the renewal', body: 'The agent contacts your engineer and tenant, finds a date that suits both and sends the invitations.' },
  { title: 'Keeps the evidence', body: 'The new certificate replaces the old one, with the full history kept and ready for inspection.' },
];

const agentLinkSx = {
  color: C.accent, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: '3px',
  '&:hover': { opacity: 0.8 },
};

const HowItWorksPage: React.FC = () => {
  const flowRef = useRef<HTMLElement>(null);
  const { hash } = useLocation();
  useEffect(() => {
    if (hash === '#renewal-flow') {
      flowRef.current?.scrollIntoView({ block: 'start' });
      flowRef.current?.focus({ preventScroll: true });
    }
  }, [hash]);
  useMarketingFonts();
  useSeoHead({
    title: 'How MillOS works — Certificates & Accounting agents',
    description: 'Meet the MillOS agents. See how Certificates reads documents, tracks expiry dates and coordinates renewals, while Accounting organises rent, expenses and quarterly figures.',
    canonicalPath: '/how-it-works',
  });

  return (
    <ThemeProvider theme={homepageTheme}>
      <Box className="lv-marketing" sx={{ ...homepageStyles, '--website-eyebrow-scale': 0.85, background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'clip' }}>
        <MarketingNav />

        <Box component="header" sx={{ maxWidth: 960, mx: 'auto', px: 3, pt: { xs: '120px', md: '144px' }, pb: 2, textAlign: 'center' }}>
          <Eyebrow>How it works</Eyebrow>
          <Box component="h1" sx={{ fontFamily: F.display, fontSize: 'clamp(2.6rem, 6vw, 4.5rem)', lineHeight: 1.06, letterSpacing: '-0.035em', my: '24px' }}>
            Two agents. Less admin.<br /><Box component="em" sx={{ fontStyle: 'italic' }}>One connected portfolio.</Box>
          </Box>
          <Box component="p" sx={{ maxWidth: '38rem', mx: 'auto', color: C.body, fontSize: { xs: 16, md: 18 } }}>
            <Box component="a" href="#certificates" sx={agentLinkSx}>Certificates</Box> keeps renewals moving. <Box component="a" href="#accounting" sx={agentLinkSx}>Accounting</Box> keeps your property finances organised. Here is what each agent does, and how the work connects.
          </Box>
        </Box>

        <Box component="section" id="certificates" aria-label="Agent 01 — Certificates" sx={{ scrollMarginTop: '80px' }}>
          <MillosChapterHeader agent="compliance" />
          <Box component="ol" aria-label="What the Certificates agent does" sx={{ maxWidth: 1240, mx: 'auto', mb: { xs: 5, md: 8 }, px: { xs: 3, md: 5 }, listStyle: 'none', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2,minmax(0,1fr))', lg: 'repeat(4,minmax(0,1fr))' }, gap: 2 }}>
            {certificateSteps.map((step, index) => (
              <Box component="li" key={step.title} sx={{ border: `1px solid ${C.hairline}`, borderRadius: '24px', bgcolor: C.mist, p: 3 }}>
                <Box component="span" sx={{ color: C.accent, fontFamily: F.mono, fontSize: 12 }}>0{index + 1}</Box>
                <Box component="h3" sx={{ fontFamily: F.ui, fontSize: 19, lineHeight: 1.3, mt: 2, mb: 1 }}>{step.title}</Box>
                <Box component="p" sx={{ color: C.body, fontSize: 14, m: 0 }}>{step.body}</Box>
              </Box>
            ))}
          </Box>
        </Box>

        <Box component="section" id="renewal-flow" ref={flowRef} tabIndex={-1} aria-label="Watch the certificate renewal flow" sx={{
          ...marketingDarkStyles, mx: 1.5, scrollMarginTop: '96px', borderRadius: '32px', background: C.heroGradient, color: D.text,
          maxWidth: 1392, '@media (min-width:1420px)': { mx: 'auto' }, px: { xs: 2, md: 4 }, py: { xs: 4, md: 6 },
          '& [data-millos-flow]': {
            '--website-glassDark': D.elevated, '--website-onDark': D.text,
            '--website-violetLight': D.blueSoft, '--website-mintLight': D.accent,
            '--website-onDarkMuted': D.muted,
            '--website-replayBackground': D.accent, '--website-replayHover': D.blue,
          },
        }}>
          <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center' }}>
            <Eyebrow variant="onImage">From expiry to evidence</Eyebrow>
            <Box component="h2" sx={{ fontFamily: F.display, fontSize: 'clamp(2rem,4vw,3.3rem)', lineHeight: 1.1, mt: 3, mb: 2 }}>Watch a certificate renewal</Box>
            <Box component="p" sx={{ color: D.muted, m: 0 }}>
              An expiring certificate starts the flow. MillOS checks availability with the provider and tenant, confirms the visit, then files the new certificate and tracks its next expiry.
            </Box>
          </Box>
          <Box component="p" id="renewal-flow-help" sx={{ display: { xs: 'block', lg: 'none' }, textAlign: 'center', color: D.muted, fontSize: 13, mt: 3, mb: 0 }}>
            Swipe or scroll sideways to explore the full diagram.
          </Box>
          <Box role="region" aria-label="Certificate renewal diagram" aria-describedby="renewal-flow-help" tabIndex={0} sx={{ overflowX: 'auto', overscrollBehaviorX: 'contain', mt: 2, '&:focus-visible': { outline: `2px solid ${D.accent}`, outlineOffset: 2 } }}>
            <Box sx={{ minWidth: 1000, height: 460, maxWidth: 1328, mx: 'auto', overflow: 'hidden' }}><MillosLoop /></Box>
          </Box>
          <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'center', color: D.muted, fontSize: 13 }}>
            Add the certificate and property details, choose your provider, and enable the agent to coordinate renewals. Include tenant contact details when access needs arranging.
          </Box>
        </Box>

        <Box component="section" id="accounting" aria-label="Agent 02 — Accounting" sx={{ scrollMarginTop: '80px' }}>
          <MillosChapterHeader agent="accounting" />
          <Box sx={{ maxWidth: 1100, mx: 'auto', px: { xs: 3, md: 5 } }}>
            <Box component="h3" sx={{ fontFamily: F.display, fontSize: 'clamp(1.8rem,3vw,2.5rem)', mb: 2 }}>From rent and receipts to quarterly figures</Box>
            <Box component="p" sx={{ color: C.body, maxWidth: '46rem', mb: 4 }}>
              Connect your bank read-only and bring in your invoices and receipts. Accounting matches rent to the right flat, organises expenses by property and prepares figures to share with your accountant for Making Tax Digital.
            </Box>
            <MillosRenewalLoop agent="accounting" />
            <Box component="p" sx={{ display: { xs: 'block', lg: 'none' }, color: C.muted, textAlign: 'center', fontSize: 13 }}>Swipe or scroll sideways to explore the accounting diagram.</Box>
          </Box>
        </Box>

        <Box component="section" aria-label="Get started with MillOS" sx={{ ...marketingDarkStyles, p: 1.5 }}>
          <Box sx={{ maxWidth: 1392, mx: 'auto', background: C.heroGradient, color: D.text, borderRadius: '32px', px: 3, py: { xs: 5, md: 8 }, textAlign: 'center' }}>
            <Eyebrow variant="onImage">Your next step</Eyebrow>
            <Box component="h2" sx={{ fontFamily: F.display, fontSize: 'clamp(2rem,4vw,3.3rem)', lineHeight: 1.1, mt: 3, mb: 2 }}>Let your agents get to work.</Box>
            <Box component="p" sx={{ color: D.muted }}>Try both agents free for 1 week. No card required.</Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mt: 3 }}>
              <Box component={RouterLink} to="/register" sx={{ display: 'inline-flex', alignItems: 'center', background: D.accent, color: D.surface, fontWeight: 600, borderRadius: '32px', px: 3, py: 1.75, '&:hover': { background: D.blue } }}>Start free trial →</Box>
              <Box component={RouterLink} to="/book-a-call" sx={{ display: 'inline-flex', alignItems: 'center', color: D.text, border: `1px solid ${D.muted}`, borderRadius: '32px', px: 3, py: 1.75, '&:hover': { background: D.elevated } }}>Book a call</Box>
            </Box>
          </Box>
        </Box>
        <MarketingFooter />
      </Box>
    </ThemeProvider>
  );
};

export default HowItWorksPage;
